import Page from "../../../../components/common/page/Page";
import Form from "../../../../components/common/form/Form";
import {SeasonType} from "../../../../types/season-type";
import {AgricultureWorkInvestmentFormItemQuery, AgricultureWorkInvestmentFormItemQueryVariables, AgricultureWorkInvestmentInput, AgricultureWorkInvestmentMutation, AgricultureWorkInvestmentMutationVariables, InvestmentTypes, Units, WorkerInput} from "../../../../types/graphql";
import {useEffect, useState} from "react";
import Input from "../../../../components/common/form/inputs/Input";
import DateInput from "../../../../components/common/form/inputs/date/DateInput";
import SeasonDropdown from "../../../../components/entities/app/season/inputs/SeasonDropdown";
import {DropdownInputPropsType} from "../../../../types/form-types";
import DropdownInput from "../../../../components/common/form/inputs/dropdown/DropdownInput";
import {faCalculator} from "@fortawesome/free-solid-svg-icons";
import Textarea from "components/common/form/inputs/Textarea";
import WorkersInput, { WorkersFormType } from "components/entities/app/work/WorkersInput";
import FormActions from "../../../../components/common/form/partials/FormActions";
import { gql } from "@apollo/client";
import { graph } from "config/axios-context";
import useAppContext from "utilities/hooks/useAppContext";
import { IdName } from "types/common-types";
import { useParams } from "react-router-dom";
import { ComponentStateTypes } from "components/common/ComponentState";
import { DateUtil } from "utilities/date-utilities";
import { AppRoutes } from "config/router";

type WorkLogInvestmentFormType = {
    id: number | string,
    name: string,
    workers: WorkersFormType[],
    startDate: Date,
    note?: string,
    season: SeasonType,
    unit: Units,
    unitPrice: number | string,
    quantity: number | string,
    total: number | string,
    investmentType: InvestmentTypes
}

const getItem = gql`
query AgricultureWorkInvestmentFormItem($id: Int!) {
    agricultureWorkInvestment(id: $id) {
      id,
      category,
      workers {
        items {
          person {
            id,
            name
          }
          id
          price
        }
      }
      startDate,
      season {
        id
        name,
        active
      }
      note
      name
      unit
      unitPrice
      quantity
      total
      investmentType
    }
    season(where: {active: {eq: true}}) {
      id,
      name,
      active
    }
  }
`;

const saveMutation = gql`
mutation AgricultureWorkInvestment($model: AgricultureWorkInvestmentInput!) {
    agricultureWorkInvestment(model: $model) {
      id
    }
  }
`;

const AgricultureInvestmentFormPage = () => {
    const context = useAppContext();
    const params = useParams<{id: string}>();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [model, setModel] = useState<Partial<WorkLogInvestmentFormType>>({
        workers: [],
        startDate: new Date(),
        id: 0,
        investmentType: undefined,
        name: '',
        note: '',
        quantity: '',
        season: undefined,
        total: '',
        unit: undefined,
        unitPrice: ''
    });

    useEffect(() => {
        loadData().then();
    }, [])

    // #region configs
    const unitDropDownConfig: DropdownInputPropsType<Units> = {
        label: 'fields.unit.name',
        required: true,
        onChange: v => insertIntoModel('unit', v),
        dataSource: {
            data: [
                Units.Item,
                Units.Kg,
                Units.L
            ]
        },
        getItemId: i => i,
        view: {
            inputView: i => i
        }
    }

    const investmentTypeDropdownConfig: DropdownInputPropsType<InvestmentTypes> = {
        label: 'fields.investmentType.name',
        required: true,
        onChange: v => insertIntoModel('investmentType', v),
        dataSource: {
            data: [
                InvestmentTypes.Chemicals,
                InvestmentTypes.Equipments,
                InvestmentTypes.Other,
            ]
        },
        getItemId: i => i,
        view: {
            inputView: i => i
        }
    }

    const calculateTotal = (force?: boolean) => {
        const canChange = force || !model.total;
        if(Number(model.quantity) && Number(model.unitPrice) && canChange) {
            model.total = Number(model.quantity) * Number(model.unitPrice);
            insertIntoModel('total', model.total);
        }
    }

    const calculateUnitPrice = (force?: boolean) => {
        const canChange = force || !model.unitPrice;
        if(Number(model.quantity) && Number(model.total) && canChange) {
            model.unitPrice =  Number(model.total) / Number(model.quantity);
            insertIntoModel('unitPrice', model.unitPrice);
        }
    }
    // #endregion

    const loadData = async () => {
        const id = params.id;
        try {
            const response = await graph<AgricultureWorkInvestmentFormItemQuery, AgricultureWorkInvestmentFormItemQueryVariables>({
                query: getItem,
                variables: {
                    id: Number(id) || 0
                }
            });

            if(response.errors) {
                context.notify('error', response.errors[0].message);
                throw response.errors;
            }

            let theModel: Partial<WorkLogInvestmentFormType> = {
                ...model,
                season: response.data!.season! as SeasonType
            }

            if(response.data?.agricultureWorkInvestment) {
                theModel = {
                    id: response.data.agricultureWorkInvestment.id,
                    name: response.data.agricultureWorkInvestment.name,
                    investmentType: response.data.agricultureWorkInvestment.investmentType,
                    note: response.data.agricultureWorkInvestment.note || '',
                    quantity: response.data.agricultureWorkInvestment.quantity,
                    startDate: new Date(response.data.agricultureWorkInvestment.startDate),
                    season: response.data.agricultureWorkInvestment.season,
                    total: response.data.agricultureWorkInvestment.total,
                    unit: response.data.agricultureWorkInvestment.unit,
                    unitPrice: response.data.agricultureWorkInvestment.unitPrice,
                    workers: response.data.agricultureWorkInvestment.workers!.items!.map(i => {
                        return {
                            id: i.id,
                            person: i.person as IdName,
                            price: i.price,
                        } as WorkersFormType;
                    })
                }
            }

            setModel(theModel);
            setComponentState('');
        } catch (error) {
            context.notify('error', 'error.loading-data');
            setComponentState('error');
            throw error;
        }
    }

    const insertIntoModel = (prop: keyof WorkLogInvestmentFormType, value: any) => {
        model[prop] = value;
        setModel({
            ...model
        });
    }

    const onSubmit = async () => {
        setIsSubmitting(true);
        const modelDto: AgricultureWorkInvestmentInput = {
            name: model.name!,
            note: model.note,
            startDate: DateUtil.toIsoDate(model.startDate!),
            seasonId: model.season!.id,
            id: Number(model.id) || 0,
            unit: model.unit!,
            unitPrice: Number(model.unitPrice!),
            quantity: Number(model.quantity!),
            total: Number(model.total!),
            investmentType: model.investmentType!,
            workers: model.workers?.map(w => {
                return {
                    id: Number(w.id) || 0,
                    personId: Number(w.person!.id),
                    date: DateUtil.toIsoDate(model.startDate),
                    price: Number(model.total!),
                    workId: Number(model.id) || 0,
                } as WorkerInput
            }) || []
        };

        try {
            const response = await graph<AgricultureWorkInvestmentMutation, AgricultureWorkInvestmentMutationVariables>({
                query: saveMutation,
                variables: {
                    model: modelDto
                }
            });

            if(response.errors) {
                context.notify('error', 'messages.submit.error');
                throw response.errors;
            }

            context.notify('success', 'messages.submit.success');
            context.navigate(AppRoutes.AGRICULTURE_INVESTMENT_VIEW, {id: response.data!.agricultureWorkInvestment!.id}, {
                replace: true
            });
        } catch (e) {
            context.notify('error', 'messages.submit.error');
            throw e;
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Page title="pages.agriculture.work.investment.form" state={componentState}>
            <Form name="agricultureWorkInvestmentFormPage" onSubmit={onSubmit}>
                <fieldset>
                    <SeasonDropdown
                        value={model.season}
                        required
                        label="fields.season.name"
                        onChange={s => insertIntoModel('season', s)}
                    />
                    <Input
                        name="name"
                        value={model.name}
                        onChange={v => insertIntoModel('name', v)}
                        required
                        maxLength={128}
                        minLength={2}
                        label="fields.name.name"
                    />
                    <DateInput
                        required
                        label="fields.date.name"
                        value={model.startDate}
                        name="startDate"
                        onChange={d => insertIntoModel('startDate', d)}
                    />
                    <DropdownInput
                        {...investmentTypeDropdownConfig}
                        value={model.investmentType}
                    />
                </fieldset>
                <fieldset>
                    <Input
                        name="quantity"
                        required
                        label="fields.quantity.name"
                        type="number"
                        value={model.quantity?.toString() || ''}
                        onChange={v => insertIntoModel('quantity', v)}
                    />
                    <DropdownInput
                        {...unitDropDownConfig}
                        value={model.unit}
                    />
                    <Input
                        name="unitPrice"
                        required
                        label="fields.unitPrice.name"
                        type="number"
                        onFocus={e => calculateUnitPrice()}
                        value={model.unitPrice?.toString() || ''}
                        onChange={v => insertIntoModel('unitPrice', v)}
                        suffix={{icon: faCalculator, name: 'unitPrice', onClick: e => calculateUnitPrice(true)}}
                    />
                    <Input
                        name="total"
                        required
                        label="fields.total.name"
                        type="number"
                        value={model.total?.toString() || ''}
                        onFocus={e => calculateTotal()}
                        onChange={v => insertIntoModel('total', v)}
                        suffix={{icon: faCalculator, name: 'total', onClick: e => calculateTotal(true)}}
                    />
                </fieldset>
                <fieldset>
                    <WorkersInput
                        name="agricultureWorkInvestmentFormPage"
                        value={model.workers}
                        required
                        hidePrice
                        onChange={v => insertIntoModel('workers', v)}
                    />
                </fieldset>
                <fieldset>
                    <Textarea label="fields.note.name" name="note" value={model.note} onChange={v => setModel({ ...model, note: v })} />
                </fieldset>
                <FormActions disableInputs={isSubmitting} />
            </Form>
        </Page>
    );
}

export default AgricultureInvestmentFormPage;
